import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';

import type {
  CreateReportPresetInput,
  ReportDataset,
  ReportFieldId,
  ReportFilter,
  ReportLayout,
  ReportPreset,
  ReportSort,
} from '../../store/types.js';

const TASK_FIELDS: readonly ReportFieldId[] = [
  'wbs', 'name', 'start', 'finish', 'duration', 'work', 'percentComplete', 'cost',
  'actualCost', 'remainingCost', 'variance', 'isCritical', 'isMilestone',
];
const RESOURCE_FIELDS: readonly ReportFieldId[] = [
  'name', 'type', 'group', 'maxUnits', 'work', 'cost', 'actualCost', 'remainingCost',
  'isOverallocated', 'isBudget',
];
const TASK_FILTERS: readonly ReportFilter[] = [
  'all', 'active', 'completed', 'late', 'critical', 'milestone',
];
const RESOURCE_FILTERS: readonly ReportFilter[] = ['all', 'overallocated', 'budget'];
const NUMERIC_FIELDS = new Set<ReportFieldId>([
  'duration', 'work', 'percentComplete', 'cost', 'actualCost', 'remainingCost', 'variance', 'maxUnits',
]);

export interface ReportPresetDialogProps {
  readonly isOpen: boolean;
  readonly preset: ReportPreset | null;
  readonly existingPresets: readonly ReportPreset[];
  readonly onSave: (preset: CreateReportPresetInput | ReportPreset) => void;
  readonly onDelete: (id: string) => void;
  readonly onDuplicate: (id: string) => void;
  readonly onCancel: () => void;
}

function defaultDraft(): CreateReportPresetInput {
  return {
    name: '',
    dataset: 'Tasks',
    layout: 'Table',
    fields: ['wbs', 'name', 'start', 'finish'],
    filter: 'all',
    groupBy: 'outlineLevel',
    sorts: [],
    outlineLevel: null,
    printOrientation: 'landscape',
  };
}

function copyPreset(preset: ReportPreset): CreateReportPresetInput {
  return {
    ...preset,
    fields: [...preset.fields],
    sorts: preset.sorts.map((sort) => ({ ...sort })),
  };
}

function moveField(fields: readonly ReportFieldId[], index: number, offset: -1 | 1): readonly ReportFieldId[] {
  const destination = index + offset;
  if (destination < 0 || destination >= fields.length) return fields;
  const next = [...fields];
  [next[index], next[destination]] = [next[destination]!, next[index]!];
  return next;
}

export function ReportPresetDialog({
  isOpen,
  preset,
  existingPresets,
  onSave,
  onDelete,
  onDuplicate,
  onCancel,
}: ReportPresetDialogProps): React.JSX.Element | null {
  const [draft, setDraft] = useState<CreateReportPresetInput>(defaultDraft);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setDraft(preset === null ? defaultDraft() : copyPreset(preset));
      setError(null);
      setConfirmDelete(false);
      restoreRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      nameRef.current?.focus();
    }
    wasOpen.current = isOpen;
  }, [isOpen, preset]);

  useEffect(() => {
    if (!isOpen) return;
    return () => {
      restoreRef.current?.focus();
      restoreRef.current = null;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const availableFields = draft.dataset === 'Tasks' ? TASK_FIELDS : RESOURCE_FIELDS;
  const availableFilters = draft.dataset === 'Tasks' ? TASK_FILTERS : RESOURCE_FILTERS;

  const toggleField = (field: ReportFieldId): void => {
    setDraft((current) => ({
      ...current,
      fields: current.fields.includes(field)
        ? current.fields.filter((candidate) => candidate !== field)
        : [...current.fields, field],
    }));
  };

  const validate = (): boolean => {
    if (draft.name.trim() === '') {
      setError('請輸入預設名稱。');
      return false;
    }
    const duplicateName = existingPresets.some((candidate) =>
      candidate.id !== preset?.id &&
      candidate.name.trim().toLocaleLowerCase() === draft.name.trim().toLocaleLowerCase(),
    );
    if (duplicateName) {
      setError('預設名稱已存在。');
      return false;
    }

    const numericCount = draft.fields.filter((field) => NUMERIC_FIELDS.has(field)).length;
    const labelCount = draft.fields.length - numericCount;
    if (draft.layout === 'Table' && draft.fields.length === 0) {
      setError('表格至少需要一個欄位。');
      return false;
    }
    if (draft.layout === 'Chart' && (labelCount === 0 || numericCount < 1)) {
      setError('圖表需要一個標籤欄位及一個數值欄位。');
      return false;
    }
    if (draft.layout === 'Comparison' && (labelCount === 0 || numericCount < 2)) {
      setError('比較需要一個標籤欄位及兩個數值欄位。');
      return false;
    }
    return true;
  };

  const save = (): void => {
    if (!validate()) return;
    const next = { ...draft, name: draft.name.trim() };
    onSave(preset === null ? next : { ...next, id: preset.id });
  };

  const cancelDelete = (): void => {
    setConfirmDelete(false);
    queueMicrotask(() => deleteButtonRef.current?.focus());
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (confirmDelete) cancelDelete();
      else onCancel();
      return;
    }
    if (event.key !== 'Tab') return;

    const root = dialogRef.current;
    const nodes = root?.querySelectorAll<HTMLElement>(
      'button:not([disabled]),input:not([disabled]),select:not([disabled])',
    );
    if (!nodes?.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  if (confirmDelete) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-3" role="presentation">
        <div
          ref={dialogRef}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="delete-report-title"
          aria-describedby="delete-report-description"
          onKeyDown={onKeyDown}
          className="w-full max-w-md rounded-lg bg-surface p-5 text-fg shadow-xl"
        >
          <h2 id="delete-report-title" className="text-lg font-semibold">確認刪除報表預設</h2>
          <p id="delete-report-description" className="mt-2">確定刪除「{preset?.name}」？</p>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" autoFocus onClick={cancelDelete}>取消刪除</button>
            <button type="button" onClick={() => preset && onDelete(preset.id)}>確認刪除</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-3" role="presentation">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-preset-title"
        onKeyDown={onKeyDown}
        className="max-h-full w-full max-w-3xl overflow-y-auto rounded-lg bg-surface p-5 text-fg shadow-xl"
      >
        <div className="flex justify-between gap-3">
          <h2 id="report-preset-title" className="text-lg font-semibold">
            {preset === null ? '建立報表預設' : '編輯報表預設'}
          </h2>
          <button type="button" aria-label="關閉報表預設" onClick={onCancel}>×</button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label>名稱
            <input ref={nameRef} aria-label="預設名稱" value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} className="mt-1 w-full rounded border border-line bg-surface p-1.5" />
          </label>
          <label>資料集
            <select
              aria-label="資料集"
              value={draft.dataset}
              onChange={(event) => {
                const dataset = event.target.value as ReportDataset;
                setDraft({
                  ...draft,
                  dataset,
                  fields: dataset === 'Tasks' ? ['wbs', 'name'] : ['name', 'type'],
                  filter: 'all',
                  groupBy: dataset === 'Tasks' ? 'outlineLevel' : 'none',
                  outlineLevel: null,
                  sorts: [],
                });
              }}
              className="mt-1 w-full rounded border border-line bg-surface p-1.5"
            >
              <option>Tasks</option><option>Resources</option>
            </select>
          </label>
          <label>版面
            <select aria-label="版面" value={draft.layout} onChange={(event) => setDraft({ ...draft, layout: event.target.value as ReportLayout })} className="mt-1 w-full rounded border border-line bg-surface p-1.5">
              <option>Table</option><option>Chart</option><option>Comparison</option>
            </select>
          </label>
          <label>篩選
            <select aria-label="篩選" value={draft.filter} onChange={(event) => setDraft({ ...draft, filter: event.target.value as ReportFilter })} className="mt-1 w-full rounded border border-line bg-surface p-1.5">
              {availableFilters.map((filter) => <option key={filter}>{filter}</option>)}
            </select>
          </label>
          <label>群組
            <select aria-label="群組" disabled={draft.dataset !== 'Tasks'} value={draft.groupBy} onChange={(event) => setDraft({ ...draft, groupBy: event.target.value as 'none' | 'outlineLevel' })} className="mt-1 w-full rounded border border-line bg-surface p-1.5">
              <option value="none">none</option><option value="outlineLevel">outlineLevel</option>
            </select>
          </label>
          <label>大綱層級
            <input aria-label="大綱層級" type="number" min="1" value={draft.outlineLevel ?? ''} onChange={(event) => setDraft({ ...draft, outlineLevel: event.target.value === '' ? null : Number(event.target.value) })} className="mt-1 w-full rounded border border-line bg-surface p-1.5" />
          </label>
          <label>列印方向
            <select aria-label="列印方向" value={draft.printOrientation} onChange={(event) => setDraft({ ...draft, printOrientation: event.target.value as 'portrait' | 'landscape' })} className="mt-1 w-full rounded border border-line bg-surface p-1.5">
              <option>portrait</option><option>landscape</option>
            </select>
          </label>
        </div>

        <fieldset className="mt-4">
          <legend className="font-medium">欄位</legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {availableFields.map((field) => (
              <label key={field} className="rounded border border-line px-2 py-1 text-sm">
                <input type="checkbox" checked={draft.fields.includes(field)} onChange={() => toggleField(field)} /> {field}
              </label>
            ))}
          </div>
          {draft.fields.length > 0 ? (
            <ol aria-label="欄位順序" className="mt-3 space-y-1 text-sm">
              {draft.fields.map((field, index) => (
                <li key={field} className="flex items-center gap-2 rounded bg-raised px-2 py-1">
                  <span className="mr-auto">{index + 1}. {field}</span>
                  <button type="button" aria-label={`${field} 上移`} disabled={index === 0} onClick={() => setDraft({ ...draft, fields: moveField(draft.fields, index, -1) })}>↑</button>
                  <button type="button" aria-label={`${field} 下移`} disabled={index === draft.fields.length - 1} onClick={() => setDraft({ ...draft, fields: moveField(draft.fields, index, 1) })}>↓</button>
                </li>
              ))}
            </ol>
          ) : null}
        </fieldset>

        <section className="mt-4">
          <h3 className="font-medium">排序</h3>
          {draft.sorts.map((sort, index) => (
            <div key={`${sort.field}-${index}`} className="mt-2 flex gap-2">
              <select aria-label={`排序欄位 ${index + 1}`} value={sort.field} onChange={(event) => setDraft({ ...draft, sorts: draft.sorts.map((item, candidateIndex) => candidateIndex === index ? { ...item, field: event.target.value as ReportFieldId } : item) })} className="rounded border border-line bg-surface p-1">
                {availableFields.map((field) => <option key={field}>{field}</option>)}
              </select>
              <select aria-label={`排序方向 ${index + 1}`} value={sort.direction} onChange={(event) => setDraft({ ...draft, sorts: draft.sorts.map((item, candidateIndex) => candidateIndex === index ? { ...item, direction: event.target.value as ReportSort['direction'] } : item) })} className="rounded border border-line bg-surface p-1">
                <option>asc</option><option>desc</option>
              </select>
              <button type="button" onClick={() => setDraft({ ...draft, sorts: draft.sorts.filter((_, candidateIndex) => candidateIndex !== index) })}>移除排序</button>
            </div>
          ))}
          <button type="button" className="mt-2" onClick={() => setDraft({ ...draft, sorts: [...draft.sorts, { field: draft.fields[0] ?? availableFields[0]!, direction: 'asc' }] })}>新增排序</button>
        </section>

        {error ? <p role="alert" className="mt-3 text-danger">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          {preset === null ? null : (
            <>
              <button type="button" onClick={() => onDuplicate(preset.id)}>複製</button>
              <button ref={deleteButtonRef} type="button" onClick={() => setConfirmDelete(true)}>刪除</button>
            </>
          )}
          <button type="button" onClick={onCancel}>取消</button>
          <button type="button" onClick={save}>儲存預設</button>
        </div>
      </div>
    </div>
  );
}

export default ReportPresetDialog;
